import { NestedArea } from "@habity/types";
import { createLabel } from "./labels";
import slugify from "slugify";
import { ROUTES } from "@habity/constants";
import { getMunicipalityImageUrl } from "./municipalities";

export const getAreaStatistics = (area: NestedArea) => {
  if (!area) {
    return [];
  }

  const { homes = [], brokers = [] } = area;

  const homesCount = homes.length;

  const homesWithPrice = homes.filter((home) => home.price);
  const avgPrice =
    homesWithPrice.length > 0
      ? Math.round(
          homesWithPrice.reduce((sum, home) => sum + (home?.price || 0), 0) /
            homesWithPrice.length
        )
      : 0;

  const priceDevPercent = 5.2;

  const brokerCounts = homes.reduce<Record<string, number>>((acc, home) => {
    const brokerId = home.responsible_broker_id?.toString();
    if (brokerId) {
      acc[brokerId] = (acc[brokerId] || 0) + 1;
    }
    return acc;
  }, {});

  let mostActiveBrokerId: string | null = null;
  let mostActiveCount = 0;
  Object.entries(brokerCounts).forEach(([id, count]) => {
    if (count > mostActiveCount) {
      mostActiveBrokerId = id;
      mostActiveCount = count as number;
    }
  });

  const mostActiveBroker = brokers.find(
    (broker) => broker.id === parseInt(mostActiveBrokerId as string)
  );
  const mostActiveBrokerName = mostActiveBroker
    ? mostActiveBroker.name
    : "Okänd";

  const formatPrice = (price: number) => {
    if (!price) {
      return "N/A";
    }

    return "SEK " + (price / 1000000).toFixed(1) + " million";
  };

  return [
    {
      key: "homes_count",
      label: "Number of homes",
      value: `${homesCount} st`,
    },
    {
      key: "avg_price",
      label: "Average price",
      value: formatPrice(avgPrice),
    },
    {
      key: "price_development",
      label: "Price development last year",
      value: `+${priceDevPercent}%`,
    },
    {
      key: "most_active_broker",
      label: "Most active brokers",
      value: mostActiveBrokerName,
    },
  ];
};

/**
 * Minimal area type for path generation
 * Only requires id and name fields
 */
export type AreaPathInput = Pick<NestedArea, 'id' | 'name'>;

/**
 * Generate a slug for an area
 * @param area The area object (only needs id and name)
 * @returns A slugified string for the area
 */
export const getAreaSlug = (area: AreaPathInput) => {
  const { id, name } = area || {};
  if (!id || !name) return "";

  const nameText = slugify(name, { lower: true });

  return `${nameText}-${id}`;
};

/**
 * Get the path to an area's profile page
 * @param area The area object (only needs id and name)
 * @param searchParams Optional search parameters to add to the URL
 * @returns The path to the area's profile page
 */
export const getAreaPath = (
  area: AreaPathInput,
  searchParams?: Record<string, string | number | boolean>
) => {
  if (!area || !area.id || !area.name) return "/areas";

  const slug = getAreaSlug(area);
  const basePath = `${ROUTES.PROFILES.AREAS}${slug}`;

  // If search params are provided, add them to the URL
  if (searchParams && Object.keys(searchParams).length > 0) {
    const urlParams = new URLSearchParams();

    // Add each parameter to the URL
    Object.entries(searchParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        urlParams.set(key, String(value));
      }
    });

    // Return the path with search parameters
    return `${basePath}?${urlParams.toString()}`;
  }

  return basePath;
};

export const getAreaImageUrl = (area: NestedArea) => {
  if (!area || !area.id) return "";

  // Check if the area has an image
  if (area.image_url) {
    return area.image_url;
  }

  // If the area has a municipality, try to use its image
  if (area.municipality) {
    return getMunicipalityImageUrl(area.municipality);
  }

  // Fallback to a placeholder image based on the area name or municipality name
  const locationName = area.municipality_name || area.name || "area";
  return `https://source.unsplash.com/random/800x600/?neighborhood,${locationName.replace(/\s+/g, "-")}`;
};

export const getQueryFromArea = (area: NestedArea) => {
  if (!area) {
    return {};
  }
  const query = new URLSearchParams();

  if (area.id) {
    query.append("areaIds", area.id.toString());
    query.append("label", createLabel("area", area.id, area.name));
  }

  // Uncomment if municipality filtering is needed
  // if (area.municipality_id && area.municipality_name) {
  //   query.append("municipalityIds", area.municipality_id.toString());
  //   query.append("municipalityLabel", createMunicipalityLabel(area.municipality_id, area.municipality_name));
  // }

  return query;
};

export const isActiveArea = (homesCount: number, brokersCount: number) => {
  return homesCount > 0 || brokersCount > 0;
};
